/**
 * Carpet pack gift line items on add-to-cart.
 * Pricing is handled by Shopify automatic discounts — no discount codes applied here.
 * APC fallback = 8 fl oz / Orange on all-purpose-cleaner-lemon.
 */
(function () {
  const APC_VARIANT_FALLBACK = 47787059839217;
  const DEO_VARIANT_FALLBACK = 48109743374577;

  const cartRoot = () => window.Shopify?.routes?.root || '/';

  /** Theme ProductForm would otherwise race and add only the hidden single-pack variant. */
  const markCarpetPackSubmit = (event) => {
    event.__carpetPackHandled = true;
  };

  const formUsesCarpetPackBundle = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const formId = form.id;
    if (!formId) return false;

    const packEl = document.querySelector(`carpet-deodorizer-pack[data-form-id="${CSS.escape(formId)}"]`);
    if (packEl && packEl.dataset.enabled === 'true') {
      const checked = packEl.querySelector('[data-carpet-pack-input]:checked');
      const pack = checked?.value;
      return pack === '2' || pack === '3';
    }

    return Boolean(document.querySelector(`carpet-deodorizer-bundle[data-form-id="${CSS.escape(formId)}"]`));
  };

  document.addEventListener(
    'submit',
    (event) => {
      if (formUsesCarpetPackBundle(event.target)) {
        markCarpetPackSubmit(event);
      }
    },
    true,
  );

  const patchProductForm = () => {
    const Ctor = customElements.get('product-form');
    if (!Ctor?.prototype?._onSubmit || Ctor.prototype.__carpetPackPatched) return;
    const original = Ctor.prototype._onSubmit;
    Ctor.prototype._onSubmit = async function patchedProductFormSubmit(event) {
      if (event.__carpetPackHandled) return;
      return original.call(this, event);
    };
    Ctor.prototype.__carpetPackPatched = true;
  };

  patchProductForm();
  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    customElements.whenDefined('product-form').then(patchProductForm).catch(() => {});
  }
  document.addEventListener('DOMContentLoaded', patchProductForm);

  const resolveVariantId = (raw, fallback) => {
    const id = Number.parseInt(String(raw || fallback), 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const buildGiftLineItems = ({
    apcVariantId,
    deoVariantId,
    comboVariantId,
    quantity,
    includeApc,
    includeDeo,
    includeCombo,
  }) => {
    const gifts = [];
    const qty = Math.max(1, quantity || 1);

    if (includeApc) {
      const apcId = resolveVariantId(apcVariantId, APC_VARIANT_FALLBACK);
      if (apcId) gifts.push({ quantity: qty, id: apcId });
    }

    if (includeCombo) {
      const comboId = resolveVariantId(comboVariantId, null);
      if (comboId) gifts.push({ quantity: qty, id: comboId });
    } else if (includeDeo) {
      const deoId = resolveVariantId(deoVariantId, DEO_VARIANT_FALLBACK);
      if (deoId) gifts.push({ quantity: qty, id: deoId });
    }

    return gifts;
  };

  const addSingleLine = async (item) => {
    const root = cartRoot();
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(item),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.warn('[carpet-pack-gifts] line add failed', item, json);
      return false;
    }
    return true;
  };

  const cartHasVariant = (cart, variantId) =>
    (cart?.items || []).some((line) => Number(line.variant_id) === Number(variantId));

  const dispatchCartEvents = (cart, addJson) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: addJson.items || [addJson], cart },
      }),
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart },
      }),
    );
  };

  /**
   * Add bundle, then each gift in its own request (deo was dropped when batched with 3 lines).
   * Shopify automatic discounts apply after lines are in cart.
   */
  const addBundleWithGifts = async ({
    bundleItem,
    apcVariantId,
    deoVariantId,
    comboVariantId,
    includeApc = false,
    includeDeo = false,
    includeCombo = false,
    quantity = 1,
    sectionsToBundle = ['variant-added'],
    sectionsUrl,
  }) => {
    const root = cartRoot();
    const sections = sectionsToBundle.join(',');

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }),
    );

    const bundleRes = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [bundleItem],
        sections,
        sections_url: sectionsUrl || `${root}variants/${bundleItem.id}`,
      }),
    });
    const addJson = await bundleRes.json().catch(() => ({}));
    if (!bundleRes.ok) {
      throw new Error(addJson.description || addJson.message || 'Could not add to cart.');
    }

    const giftItems = buildGiftLineItems({
      apcVariantId,
      deoVariantId,
      comboVariantId,
      quantity,
      includeApc,
      includeDeo,
      includeCombo,
    });

    for (const gift of giftItems) {
      await addSingleLine(gift);
    }

    let cart = await (await fetch(`${root}cart.js`)).json();

    if (includeCombo) {
      const comboId = resolveVariantId(comboVariantId, null);
      if (comboId && !cartHasVariant(cart, comboId)) {
        await addSingleLine({ quantity: Math.max(1, quantity || 1), id: comboId });
        cart = await (await fetch(`${root}cart.js`)).json();
      }
    } else if (includeDeo) {
      const deoId = resolveVariantId(deoVariantId, DEO_VARIANT_FALLBACK);
      if (deoId && !cartHasVariant(cart, deoId)) {
        await addSingleLine({ quantity: Math.max(1, quantity || 1), id: deoId });
        cart = await (await fetch(`${root}cart.js`)).json();
      }
    }

    cart.sections = addJson.sections;
    dispatchCartEvents(cart, addJson);
    return { cart, addJson };
  };

  window.CarpetPackGifts = {
    markCarpetPackSubmit,
    buildGiftLineItems,
    addBundleWithGifts,
  };
})();
